import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileText, ArrowRight, Utensils, CheckCircle } from "lucide-react";
import { useAuth, API } from "@/App";

const PostOnboardingPrompt = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(true);

  // B2B users or users with care team go straight to dashboard
  useEffect(() => {
    if (user?.user_type === "b2b" || user?.has_care_team) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleUpload = () => {
    // Mark that user saw this prompt
    localStorage.setItem("meal_plan_prompt_seen", "true");
    navigate("/my-meal-plans");
  };

  const handleSkip = () => {
    // Mark that user saw this prompt
    localStorage.setItem("meal_plan_prompt_seen", "true");
    navigate("/dashboard");
  };

  // Don't render for B2B users
  if (user?.user_type === "b2b" || user?.has_care_team) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-lg rounded-3xl border border-orange-100/50 shadow-soft-lift">
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <CardTitle className="font-heading text-2xl font-bold text-secondary">
            You're All Set, {user?.name?.split(' ')[0] || 'there'}!
          </CardTitle>
          <CardDescription className="text-muted-foreground text-base">
            Your profile is ready. One more thing that can help...
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-4">
          {/* Meal Plan Upload Prompt */}
          <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl p-5 border border-purple-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-secondary mb-1">
                  Do you have a meal plan?
                </h3>
                <p className="text-sm text-muted-foreground">
                  If your dietitian gave you a meal plan, you can upload it now. We'll help you track your meals and stay on plan.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={handleUpload}
              className="w-full rounded-full bg-primary text-white hover:bg-primary/90 py-6 font-bold"
              data-testid="upload-meal-plan-btn"
            >
              <Upload className="w-5 h-5 mr-2" />
              Yes, Upload My Meal Plan
            </Button>
            
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="w-full rounded-full text-muted-foreground hover:text-secondary py-6"
              data-testid="skip-meal-plan-btn"
            >
              Skip for Now
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            You can always upload a meal plan later from your dashboard.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PostOnboardingPrompt;
